import type {
  DecisionContext,
  DecisionExecutionEnvelopeReference,
  DecisionContextUserSnapshot,
} from "../types/decision-context";

interface DecisionWorkspaceContext {
  id?: string | null;
  deskId?: string | null;
  deskSlug?: string | null;
  title?: string | null;
  surfaceId?: string | null;
}

export interface BuildDecisionContextInput {
  surfaceId?: string | null;
  conversationId?: string | null;
  connectorScopeId?: string | null;
  agentTaskId?: string | null;
  workspace?: DecisionWorkspaceContext | null;
  userContext?: DecisionContextUserSnapshot;
  userMode?: string | null;
  persona?: string | null;
}

export function buildFrontendDecisionContext({
  surfaceId,
  conversationId,
  connectorScopeId,
  agentTaskId,
  workspace,
  userContext,
  userMode,
  persona,
}: BuildDecisionContextInput): DecisionContext {
  const selected = userContext?.selectedSymbol;
  const symbol = stringValue(selected?.symbol);
  return {
    ids: {
      deskId:
        stringValue(workspace?.deskId) ?? stringValue(workspace?.deskSlug),
      userId: null,
      surfaceId: stringValue(surfaceId ?? workspace?.surfaceId),
      conversationId: stringValue(conversationId),
      narrativeSessionId:
        surfaceId === "narrativeflow" ? stringValue(workspace?.id) : null,
      briefId: null,
      agentTaskId: stringValue(agentTaskId),
      connectorScopeId: stringValue(connectorScopeId),
    },
    snapshot: {
      observationIds: [],
      sourceHashes: [],
      sourceTiers: [],
      observedAt: null,
      instrumentScope: symbol ? [symbol] : [],
      marketSnapshot: null,
      sessionRegime: null,
      activeNarrative:
        surfaceId === "narrativeflow"
          ? {
              id: stringValue(workspace?.id),
              title: stringValue(workspace?.title),
            }
          : null,
      userMode: stringValue(userMode),
      persona: stringValue(persona),
      riskLimits: userContext?.riskSettings ?? null,
      lockoutState: null,
      emotionalState: null,
      signalSpecId: null,
      briefSlot: null,
      briefChecksum: null,
    },
  };
}

export function readDecisionUserContext(): DecisionContextUserSnapshot {
  return {
    traderName: readSetting("traderName"),
    selectedSymbol: readSetting("selectedSymbol") as
      DecisionContextUserSnapshot["selectedSymbol"] | undefined,
    tradingGoals: readSetting("tradingGoals"),
    instrumentsTraded: readSetting("instrumentsTraded"),
    riskSettings: readSetting("riskSettings") as
      Record<string, unknown> | undefined,
  };
}

export function readDecisionExecutionEnvelopeReference(
  value: unknown,
): DecisionExecutionEnvelopeReference | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const model = record.interactiveModel as Record<string, unknown> | undefined;
  const invocationId = stringValue(record.invocationId);
  const contextDigest = stringValue(record.contextDigest);
  const provider = stringValue(model?.provider);
  const modelId = stringValue(model?.model);
  if (
    !invocationId ||
    !contextDigest ||
    record.engine !== "fintheon_durable" ||
    !provider ||
    !modelId
  ) {
    return null;
  }
  return {
    invocationId,
    contextDigest,
    engine: "fintheon_durable",
    interactiveModel: {
      provider,
      model: modelId,
    },
  };
}

function readSetting(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch {
    return undefined;
  }
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
